// get-cell-content.ts

import { GridCell, GridCellKind } from '@glideapps/glide-data-grid'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { ColumnAdapter } from '@makyoapp/makyo-common'

import { ICell } from '@/core/models/columns'
import { IFunctionCell } from '@/core/models/columns/function'
import { ISingleCellCell } from '@/core/models/columns/single-cell'
import { IRow } from '@/core/models/tables/row'
import { CustomGridCellKind } from '@/components/GridV2/helpers/CustomGridCellKind'

import {
  createAttachmentCell,
  createBooleanCell,
  createConnectionCell,
  createCoordinateCell,
  createCustomCell,
  createLookupCell,
  createMathCell,
  createNumberCell,
  createRichTextCell,
  createTemplateCell,
  createTextCell,
  createUrlCell,
} from './cell-creators'

const DeferredColumnTypes = [
  ColumnTypes.Connection,
  ColumnTypes.Lookup,
  ColumnTypes.Join,
  ColumnTypes.Function,
  ColumnTypes.Math,
  ColumnTypes.Template,
  ColumnTypes.SingleCell,
]

export const getCellContent = (
  column: ColumnAdapter,
  row: IRow,
  themeMode: string,
  dataEditorRef: any,
  globalReadOnly?: boolean // only for snapshot tables
) => {
  const cell = row[column.id] as ICell
  const columnType = column.type

  // display skeleton for deferred column types.
  // initially this cell will act as TextCell. After the engine run,
  // it will then be converted into the corresponding cell (connection, lookup, etc),
  // thats why we need to show the loading skeleton here, not in `createCustomCell` function.
  if (DeferredColumnTypes.includes(columnType)) {
    const isMathLoading = columnType === ColumnTypes.Math && cell?.value === null
    const isSingleCellLoading = columnType === ColumnTypes.SingleCell && cell?.value === null

    if (cell?.value === '_CELL_LOADING_' || isMathLoading || isSingleCellLoading) {
      return {
        kind: GridCellKind.Custom,
        allowOverlay: false,
        readonly: true,
        data: {
          kind: CustomGridCellKind.Spinner,
          exstra: { theme: themeMode },
        },
        copyData: '',
      } as GridCell
    }
  }

  if (!cell) return { kind: GridCellKind.Loading } as GridCell

  // For function result we will display the cell based on its return type
  if (columnType === ColumnTypes.Function) {
    const fnCell = cell as IFunctionCell
    const returnType = fnCell.returnType
    const returnCell = fnCell.toReturnTypeCell()
    if (returnType) {
      return renderCell(returnCell, returnType, themeMode, dataEditorRef, true)
    }
  }

  if (columnType === ColumnTypes.SingleCell) {
    const singleCell = cell as ISingleCellCell
    const resultType = singleCell.resolvedType ?? ColumnTypes.Text
    const resultCell = singleCell.value
    if (resultCell) {
      return renderCell(resultCell as any, resultType, themeMode, dataEditorRef, true)
    }
  }

  return renderCell(cell, columnType, themeMode, dataEditorRef, globalReadOnly)
}

export const renderCell = (
  cell: ICell,
  columnType: ColumnTypes,
  themeMode: string,
  dataEditorRef: any,
  readOnly?: boolean
) => {
  switch (columnType) {
    case ColumnTypes.Connection:
      return createConnectionCell(cell)
    case ColumnTypes.Lookup:
      return createLookupCell(cell)
    case ColumnTypes.Number:
      return createNumberCell(cell, readOnly)
    case ColumnTypes.Math:
      return createMathCell(cell, true)
    case ColumnTypes.URL:
      return createUrlCell(cell, dataEditorRef, readOnly)
    case ColumnTypes.Boolean:
      return createBooleanCell(cell, readOnly)
    case ColumnTypes.Coordinate:
      return createCoordinateCell(cell, readOnly)
    case ColumnTypes.Attachment:
      return createAttachmentCell(cell, dataEditorRef)
    case ColumnTypes.RichText:
      return createRichTextCell(cell, dataEditorRef)
    case ColumnTypes.Text:
    case ColumnTypes.Email:
    case ColumnTypes.Phone:
    case ColumnTypes.Address:
    case ColumnTypes.RowID:
    case ColumnTypes.Emoji:
      return createTextCell(cell, readOnly)
    case ColumnTypes.SessionID:
      return createTextCell(cell, true)
    case ColumnTypes.Template:
      return createTemplateCell(cell)
    default:
      return createCustomCell(cell, columnType, themeMode, readOnly, dataEditorRef)
  }
}
